import {
  UseMutationOptions,
  UseQueryOptions,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { capitalize } from 'lodash-es';

import { showInfo, showSuccess } from '@components/toaster';
import { useEvent } from '@hooks/use-event';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { downloadFile } from '@utils/browser';

import { useCurrentCompany } from 'api/authentication/hooks';
import { useWebSocket, waitForElasticRefresh } from 'api/hooks';
import {
  handleError,
  handlePossibleFieldError,
  handlePossibleNonFieldError,
} from 'api/query-client';
import { BulkCreateTraveller } from 'api/traveller-journeys/types';
import { EmailError } from 'api/types';
import {
  applyBaseFieldEdit,
  patchCompanyTravellerListCaches,
  reconcileAfterElasticRefresh,
  startFieldEdit,
} from 'utils/inline-edit-cache';

import {
  BulkAddToJourneyType,
  BulkErrorResponse,
  CompanyTraveller,
  CompanyTravellersUserSettings,
  CompanyTravellersUserSettingsCompatible,
  CreateCompanyTraveller,
  bulkAddCompanyTravellerToJourney,
  bulkAddCompanyTravellerToJourneyStatistics,
  bulkCreateCompanyTraveller,
  bulkDeleteCompanyTraveller,
  BulkEditFieldsResponse,
  bulkEditCompanyTravellerFields,
  deleteCompanyTraveller,
  editCompanyTraveller,
  getBulkCreateTemplate,
  getCompanyTraveller,
  loadCompanyTravellerSettings,
  loadCompanyTravellers,
  loadCompanyTravellersFull,
  updateCompanyTravellerUserSettings,
} from './requests';

export const getCompanyTravellersQueryKey = (queryString?: string) =>
  ['company-travellers', queryString].filter(Boolean);

export const useInvalidateTravellers = () => {
  const queryClient = useQueryClient();

  const invalidateJourneyTravellers = (journeyId: string) => {
    queryClient.invalidateQueries(['travellers', journeyId]);
    queryClient.invalidateQueries(['traveller']);
  };

  const invalidateCompanyTravellers = () => {
    queryClient.invalidateQueries(getCompanyTravellersQueryKey('full'));
    queryClient.invalidateQueries(['travellers']);
    queryClient.invalidateQueries(['traveller']);
    queryClient.invalidateQueries(['company-traveller']);
  };

  return { invalidateJourneyTravellers, invalidateCompanyTravellers };
};

type EditCompanyTravellerVariables = {
  shortId: string;
  data: FormData;
  applyOptimistic?: () => void;
  isStale?: () => boolean;
  isSuperseded?: () => boolean;
  isErrorShownByCaller?: boolean;
};

export const useCompanyTravellersPaginated = (
  queryString?: string,
  setCount?: (count: number) => void,
) => {
  const company = useCurrentCompany();
  const query = useQuery({
    queryKey: getCompanyTravellersQueryKey(queryString),
    enabled: queryString !== '',
    queryFn: () => loadCompanyTravellers(queryString),
    onSuccess: (data) => {
      if (setCount) setCount(data.count);
    },
    keepPreviousData: true,
  });
  const queryClient = useQueryClient();
  useWebSocket(`company-travellers/${company?.shortId}/`, () => {
    queryClient.refetchQueries(getCompanyTravellersQueryKey(queryString));
  });
  return query;
};

export const useCompanyTravellers = (
  queryOptions: { staleTime?: number; keepPreviousData?: boolean } = {},
) => {
  return useQuery({
    queryKey: getCompanyTravellersQueryKey('full'),
    queryFn: loadCompanyTravellersFull,
    ...queryOptions,
  });
};

export const useCompanyTravellersFull = () => {
  const query = useCompanyTravellers();
  const company = useCurrentCompany();
  const queryClient = useQueryClient();
  useWebSocket(`company-travellers/${company?.shortId}/`, () => {
    waitForElasticRefresh(() => {
      queryClient.refetchQueries(getCompanyTravellersQueryKey('full'));
    });
  });
  return query;
};

type CompanyTravellersUserSettingsQueryOptions = UseQueryOptions<
  CompanyTravellersUserSettingsCompatible['settings'],
  unknown,
  CompanyTravellersUserSettings['settings'],
  readonly ['company-traveller-settings']
>;

export const useCompanyTravellersSettings = (
  options?: Omit<CompanyTravellersUserSettingsQueryOptions, 'queryKey' | 'queryFn' | 'select'>,
) => {
  return useQuery({
    ...options,
    queryKey: ['company-traveller-settings'],
    queryFn: loadCompanyTravellerSettings,
  });
};

export const useUpdateCompanyTravellersSettings = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (settings: CompanyTravellersUserSettings) => {
      return updateCompanyTravellerUserSettings(settings);
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['company-traveller-settings'], data.settings);
    },
  });
};

export const useCompanyTraveller = (shortId: string) => {
  return useQuery({
    queryKey: ['company-traveller', shortId],
    queryFn: () => getCompanyTraveller(shortId),
    enabled: !!shortId,
  });
};

export const useCreateCompanyTraveller = (
  onSuccess?: (traveller: CompanyTraveller) => void,
  options?: UseMutationOptions<unknown, EmailError, any>,
) => {
  const { t } = useScopedTranslate(locale);

  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: CreateCompanyTraveller,
    onSuccess: (data) => {
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(['company-traveller']);
        queryClient.refetchQueries(getCompanyTravellersQueryKey());
      });
      showSuccess(t('travellerCreated'));
      onSuccess?.(data);
    },
    onError: (data) => {
      const message = data.response?.data.email
        ? capitalize(data.response?.data.email[0].replace(/_/g, ' '))
        : t('travellerNotCreated');
      handleError(message);
    },
  });
};

type EditCompanyTravellerTranslate = ReturnType<typeof useScopedTranslate<typeof locale>>['t'];

const makeEditCompanyTravellerMutation = ({
  t,
  queryClient,
  isOptimistic,
  onSuccess,
}: {
  t: EditCompanyTravellerTranslate;
  queryClient: ReturnType<typeof useQueryClient>;
  isOptimistic: boolean;
  onSuccess?: () => void;
}): UseMutationOptions<unknown, unknown, EditCompanyTravellerVariables> & {
  mutationFn: (variables: EditCompanyTravellerVariables) => Promise<unknown>;
} => ({
  mutationFn: ({ shortId, data }) => editCompanyTraveller(shortId, data),
  onSuccess: isOptimistic
    ? (_, variables) => {
        // No toast on the inline path: the cell shows the new value instantly,
        // and editing a run of cells would otherwise stack up toasts.
        variables.applyOptimistic?.();

        reconcileAfterElasticRefresh({
          refetch: () =>
            Promise.all([
              queryClient.refetchQueries(getCompanyTravellersQueryKey()),
              queryClient.refetchQueries(['company-traveller', variables.shortId]),
              queryClient.invalidateQueries(['travellers']),
            ]),
          isStale: variables.isStale ?? (() => false),
          isSuperseded: variables.isSuperseded,
          reapply: () => variables.applyOptimistic?.(),
        });
      }
    : async (_, variables) => {
        await waitForElasticRefresh(() => {
          queryClient.refetchQueries(['company-traveller', variables.shortId]);
          queryClient.refetchQueries(getCompanyTravellersQueryKey());
          queryClient.invalidateQueries(['travellers']);
        });

        showSuccess(t('travellerEdited'));
        onSuccess?.();
      },
  onError: (data, variables) => {
    // Every editor but the legacy free-text modal reports the failure itself,
    // in the panel it keeps open or as its own toast.
    if (variables.isErrorShownByCaller) {
      return;
    }
    handlePossibleNonFieldError(data, t('travellerNotEdited'));
  },
});

export const useEditCompanyTraveller = (onSuccess?: () => void) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();

  return useMutation(
    makeEditCompanyTravellerMutation({ t, queryClient, isOptimistic: false, onSuccess }),
  );
};

export const useInlineEditCompanyTraveller = () => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();

  return useMutation(makeEditCompanyTravellerMutation({ t, queryClient, isOptimistic: true }));
};

export const useInlineEditContactField = (field: 'email' | 'phone') => {
  const editCompanyTraveller = useInlineEditCompanyTraveller();
  const queryClient = useQueryClient();

  return useEvent(
    async (
      companyTravellerShortId: string,
      value: string,
      beforeValue: string,
      applyExtraOptimistic?: () => void,
    ) => {
      const formData = new FormData();
      formData.append(field, value);

      const fieldEdit = startFieldEdit(`${companyTravellerShortId}:${field}`, beforeValue ?? '');

      await editCompanyTraveller.mutateAsync({
        shortId: companyTravellerShortId,
        data: formData,
        isErrorShownByCaller: true,
        applyOptimistic: () => {
          patchCompanyTravellerListCaches(queryClient, companyTravellerShortId, (traveller) =>
            applyBaseFieldEdit(traveller, field, value),
          );
          applyExtraOptimistic?.();
        },
        isStale: () =>
          fieldEdit.isStale(
            queryClient
              .getQueryData<CompanyTraveller[]>(getCompanyTravellersQueryKey('full'))
              ?.find((traveller) => traveller.shortId === companyTravellerShortId)?.[field] ?? '',
          ),
        isSuperseded: fieldEdit.isSuperseded,
      });
    },
  );
};

export function useDeleteCompanyTraveller() {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const queryKey = getCompanyTravellersQueryKey('full');

  return useMutation({
    onMutate: async (shortId) => {
      await queryClient.cancelQueries(queryKey);
      const previousData = queryClient.getQueryData<Array<CompanyTraveller>>(queryKey);

      queryClient.setQueryData<Array<CompanyTraveller>>(queryKey, (old) => {
        return old?.filter((traveller) => traveller.shortId !== shortId);
      });

      return { previousData };
    },
    mutationFn: (shortId: string) => deleteCompanyTraveller(shortId),
    onSuccess: () => {
      waitForElasticRefresh(() => {
        queryClient.invalidateQueries(getCompanyTravellersQueryKey());
        queryClient.invalidateQueries(['travellers']);
      });
      showSuccess(t('travellerDeleted'));
    },
    onError: (error, data, context) => {
      handleError(t('travellerNotDeleted'), () =>
        queryClient.setQueryData(queryKey, context?.previousData),
      );
    },
  });
}
export const useBulkCreateCompanyTravellerTemplate = () => {
  return useMutation({
    mutationFn: () => getBulkCreateTemplate(),
    onSuccess: (response) => {
      downloadFile(response, 'traveller-import-template.csv');
    },
  });
};

export const useBulkCreateCompanyTraveller = (
  setErrors: (errors: { travellers?: { [key: string]: string[] }[] }) => void,
  options?: UseMutationOptions<
    unknown,
    unknown,
    { travellers: BulkCreateTraveller[]; isSave: boolean; updateExisting?: boolean }
  >,
) => {
  return useMutation({
    ...options,
    mutationFn: (data) => bulkCreateCompanyTraveller(data),
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
    },
    onError: (errors: BulkErrorResponse) => {
      handleError(() => {
        const errorData = errors.response?.data ?? {};
        if (errorData?.travellers) {
          setErrors(errorData);
        }
      });
    },
  });
};

export const useBulkDeleteCompanyTraveller = (
  options?: UseMutationOptions<unknown, unknown, { ids: string[]; selectAll: boolean }>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  const queryKey = getCompanyTravellersQueryKey('full');
  return useMutation({
    ...options,
    mutationFn: (data) => bulkDeleteCompanyTraveller(data),
    onMutate: async ({ ids }) => {
      await queryClient.cancelQueries(queryKey);
      const previousData = queryClient.getQueryData<Array<CompanyTraveller>>(queryKey);

      queryClient.setQueryData<Array<CompanyTraveller>>(queryKey, (old) => {
        return old?.filter((traveller) => !ids.includes(traveller.shortId));
      });

      return { previousData };
    },
    onSuccess: (...data) => {
      options?.onSuccess?.(...data);
      showSuccess(t('travellersDeleted'));
      waitForElasticRefresh(() => {
        queryClient.refetchQueries(getCompanyTravellersQueryKey());
      });
    },
    onError: (error, data, context) => {
      handleError(t('travellersNotDeleted'), () =>
        queryClient.setQueryData(queryKey, context?.previousData),
      );
    },
  });
};

export const useBulkEditCompanyTravellerFields = (
  options?: UseMutationOptions<BulkEditFieldsResponse, unknown, FormData>,
) => {
  const { t } = useScopedTranslate(locale);
  const { invalidateCompanyTravellers } = useInvalidateTravellers();
  return useMutation({
    ...options,
    mutationFn: (data: FormData) => bulkEditCompanyTravellerFields({ data }),
    onSuccess: (...args) => {
      const { updated } = args[0];
      if (updated === 0) {
        showInfo(t('noTravellersUpdated'));
        return;
      }
      options?.onSuccess?.(...args);
      showSuccess(updated === 1 ? t('fieldsUpdatingOne') : t('fieldsUpdating', { count: updated }));
      waitForElasticRefresh(invalidateCompanyTravellers);
    },
    onError: (error) => {
      handlePossibleFieldError(error, t('fieldsNotUpdated'));
    },
  });
};

export const useBulkAddCompanyTravellerToJourney = (
  options?: UseMutationOptions<{ started: number }, unknown, BulkAddToJourneyType>,
) => {
  const { t } = useScopedTranslate(locale);
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationFn: (data) => bulkAddCompanyTravellerToJourney(data),
    onError: () => {
      handleError(t('travellersNotAdded'));
    },
    onSuccess: (data, varibles, context) => {
      options?.onSuccess?.(data, varibles, context);
      waitForElasticRefresh(() => {
        queryClient.refetchQueries(getCompanyTravellersQueryKey());
        queryClient.invalidateQueries(['company-traveller']);
        queryClient.invalidateQueries(['traveller-journeys-status']);
      });
    },
  });
};

export const useBulkAddCompanyTravellerToJourneyStats = ({
  ids,
  journeyId,
}: {
  ids: string[];
  journeyId: string;
}) => {
  const query = `?ids=${ids.join(',')}&journeyId=${journeyId}`;
  return useQuery({
    queryKey: ['bulk-add-stats', journeyId, query],
    queryFn: () => bulkAddCompanyTravellerToJourneyStatistics(journeyId, ids),
  });
};

const locale = {
  en: {
    travellerCreated: 'Traveller created',
    travellerEdited: 'Traveller edited',
    travellerNotEdited: 'Traveller not edited',
    travellerNotCreated: 'Traveller not created',
    travellerDeleted: 'Traveller deleted',
    travellerNotDeleted: 'Traveller not deleted',
    travellersDeleted: 'Travellers deleted',
    travellersNotDeleted: 'Travellers could not be deleted',
    travellersNotAdded: 'Travellers could not be added to journey',
    fieldsUpdating: 'Updating fields for {count} travellers',
    fieldsUpdatingOne: 'Updating fields for 1 traveller',
    noTravellersUpdated: 'No travellers were updated',
    fieldsNotUpdated: 'Could not update fields',
  },
};
